/**
 * Secure API Client
 * Provides authenticated HTTP client with automatic credential handling,
 * CSRF protection, error handling, and request/response interceptors
 */

import { addCsrfHeader, ensureCsrfToken, requiresCsrfToken } from "./csrf";
import { analytics } from "../lib/analytics";
import { trace } from "../lib/trace";

interface ApiError {
  error: string;
  message: string;
  status: number;
}

interface ApiErrorPayload {
  error?: {
    message?: string;
  } | string;
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public error: string,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

interface RequestOptions extends RequestInit {
  // Automatically add credentials
  skipAuth?: boolean;
  // Custom timeout (default 30s)
  timeout?: number;
  // Skip automatic 401 redirect to /login (for auth-status checks)
  skipAuthRedirect?: boolean;
  // Internal guard to avoid infinite CSRF retry loops
  _csrfRetryAttempted?: boolean;
  // Internal guard when this request path already forced a CSRF sync
  _csrfReady?: boolean;
}

/**
 * Endpoints where a 401 is expected and should NOT trigger an automatic
 * redirect to /login. These are auth-probing endpoints whose callers
 * already handle the unauthenticated case gracefully.
 */
const AUTH_CHECK_ENDPOINTS = ["/api/auth/status", "/api/auth/csrf"];
const OPTIONAL_ANALYTICS_ENDPOINT_PATTERNS = [
  /^\/api\/observability(?:\/|$)/,
  /^\/api\/workers\/health(?:$|[/?#])/,
  /^\/api\/workers\/[^/]+\/(?:metrics|errors)(?:$|[/?#])/,
];

function isOptionalAnalyticsUnavailableResponse(
  endpoint: string,
  status: number,
  errorData: Record<string, unknown>,
): boolean {
  if (status !== 503) return false;
  if (!OPTIONAL_ANALYTICS_ENDPOINT_PATTERNS.some((pattern) => pattern.test(endpoint))) {
    return false;
  }

  const errorText = [
    String(errorData.error ?? ""),
    String(errorData.message ?? ""),
    String(errorData.code ?? ""),
  ].join(" ").toLowerCase();

  return /analytics api token not configured|analytics_not_configured/.test(errorText);
}

/** Max retries for transient GET failures (5xx / network errors) */
const RETRY_MAX = 2;
const RETRY_BASE_MS = 1000;

/** Whether a failed request should be retried */
function isRetryable(method: string, status: number, error: string): boolean {
  // Allow tests to disable retries via global flag
  if ((globalThis as any).__DISABLE_API_RETRIES__) return false;
  // Only retry safe (GET) requests
  if (method !== "GET") return false;
  // Retry on 5xx server errors
  if (status >= 500 && status < 600) return true;
  // Retry on network errors and timeouts
  if (error === "NETWORK_ERROR" || error === "TIMEOUT") return true;
  return false;
}

class SecureApiClient {
  private baseUrl: string;
  private defaultTimeout = 30000; // 30 seconds
  private redirecting = false; // Guard against multiple 401 redirects
  /** Override max retries (used by tests to disable retry logic) */
  _maxRetries = RETRY_MAX;

  constructor(baseUrl: string = "") {
    this.baseUrl = baseUrl;
  }

  /**
   * Make authenticated API request (with automatic retry for transient GET failures)
   */
  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const {
      skipAuth = false,
      skipAuthRedirect = false,
      timeout = this.defaultTimeout,
      _csrfRetryAttempted = false,
      _csrfReady = false,
      ...fetchOptions
    } = options;

    // Build full URL
    const url = endpoint.startsWith("http") ? endpoint : `${this.baseUrl}${endpoint}`;
    const method = fetchOptions.method || "GET";
    const csrfRequired = requiresCsrfToken(method);
    const isCsrfBootstrapEndpoint = endpoint.startsWith("/api/auth/csrf");

    // Default headers
    const headers = new Headers(fetchOptions.headers);
    if (!headers.has("Content-Type") && fetchOptions.body && !(fetchOptions.body instanceof FormData)) {
      headers.set("Content-Type", "application/json");
    }
    if (!headers.has("Accept")) {
      headers.set("Accept", "application/json");
    }

    // Add CSRF token for state-changing requests
    if (csrfRequired && !isCsrfBootstrapEndpoint && !_csrfReady) {
      try {
        await ensureCsrfToken();
      } catch (error: unknown) {
        trace.api("CSRF ensure failed before request", {
          endpoint,
          error: String(error),
        }, "error");
      }
      addCsrfHeader(headers);
    }

    // Always include credentials for session cookies
    const requestOptions: RequestInit = {
      ...fetchOptions,
      headers,
      credentials: skipAuth ? "omit" : "include",
    };

    // Add request timestamp for logging
    const requestId = crypto.randomUUID();
    const startTime = performance.now();
    trace.api(`${method} ${endpoint}`, { requestId, hasBody: !!fetchOptions.body });

    let lastError: ApiClientError | undefined;
    const maxAttempts = 1 + this._maxRetries; // first attempt + retries

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Exponential backoff before retry (skip on first attempt)
      if (attempt > 0) {
        const delayMs = RETRY_BASE_MS * Math.pow(2, attempt - 1);
        trace.api(`Retrying ${method} ${endpoint} (attempt ${attempt + 1}/${maxAttempts}, delay ${delayMs}ms)`, { requestId });
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }

      try {
        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const response = await fetch(url, {
          ...requestOptions,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        const duration = Math.round(performance.now() - startTime);

        trace.api(`${method} ${endpoint} → ${response.status}`, {
          requestId,
          status: response.status,
          duration_ms: duration,
        });

        // Handle 401 Unauthorized — redirect to login unless this is an
        // expected auth-check endpoint (e.g. /api/auth/status) or the
        // caller explicitly opted out via skipAuthRedirect.
        // Guard: only redirect once even if multiple parallel requests all return 401.
        if (response.status === 401) {
          const isAuthCheckEndpoint = AUTH_CHECK_ENDPOINTS.some((p) => endpoint.startsWith(p));
          if (!skipAuthRedirect && !isAuthCheckEndpoint && !this.redirecting) {
            this.redirecting = true;
            trace.api("Session expired, redirecting to /login", { requestId, endpoint });
            if (typeof window !== "undefined") {
              // Preserve the current path so the user returns after re-authenticating
              const returnTo = window.location.pathname + window.location.search;
              const loginUrl = returnTo && returnTo !== "/" && returnTo !== "/login"
                ? `/login?returnTo=${encodeURIComponent(returnTo)}`
                : "/login";

              // Show brief notification before redirect so user understands why
              const toastContainer = document.createElement("div");
              toastContainer.style.cssText =
                "position:fixed;top:16px;right:16px;z-index:9999;padding:12px 20px;background:#f59e0b;color:#fff;border-radius:8px;font-size:14px;box-shadow:0 4px 12px rgba(0,0,0,0.15);";
              toastContainer.textContent = "Session expired. Redirecting to login\u2026";
              document.body.appendChild(toastContainer);
              setTimeout(() => {
                window.location.href = loginUrl;
              }, 1500);
            }
          }
        }

        // Handle non-JSON responses
        const contentType = response.headers.get("content-type");
        if (!contentType?.includes("application/json")) {
          if (!response.ok) {
            const err = new ApiClientError(
              `HTTP ${response.status}: ${response.statusText}`,
              response.status,
              "Non-JSON response",
            );
            // Retry 5xx non-JSON responses for GET requests
            if (isRetryable(method, response.status, "") && attempt < maxAttempts - 1) {
              lastError = err;
              continue;
            }
            throw err;
          }
          return (await response.text()) as T;
        }

        const data = await response.json();

        // Handle error responses
        if (!response.ok) {
          const errorData = data as Record<string, unknown>;
          const errorMessage = (errorData.message as string) || (errorData.error as string) || `HTTP ${response.status}`;
          const csrfErrorText = `${String(errorData.error ?? "")} ${errorMessage}`.toLowerCase();
          const isCsrfValidationFailure =
            response.status === 403 &&
            csrfRequired &&
            !isCsrfBootstrapEndpoint &&
            csrfErrorText.includes("csrf");

          if (isCsrfValidationFailure && !_csrfRetryAttempted) {
            trace.api("Retrying request after CSRF resync", {
              requestId,
              endpoint,
              method,
            }, "error");
            await ensureCsrfToken({ forceServerSync: true });
            return this.request<T>(endpoint, {
              ...options,
              _csrfRetryAttempted: true,
              _csrfReady: true,
            });
          }

          if (isOptionalAnalyticsUnavailableResponse(endpoint, response.status, errorData)) {
            trace.api("Optional analytics service unavailable", {
              requestId,
              endpoint,
              status: response.status,
              error: String(errorData.error ?? ""),
            });
          } else {
            analytics.error("api_response_error", new Error(errorMessage), {
              requestId,
              status: response.status,
              error: String(errorData.error ?? ""),
            });
          }

          const err = new ApiClientError(errorMessage, response.status, (errorData.error as string) || "API Error");
          // Retry transient server errors for GET requests
          if (isRetryable(method, response.status, "") && attempt < maxAttempts - 1) {
            lastError = err;
            continue;
          }
          throw err;
        }

        return data as T;
      } catch (error: unknown) {
        if (error instanceof ApiClientError) {
          // Check if this ApiClientError is retryable
          if (isRetryable(method, error.status, error.error) && attempt < maxAttempts - 1) {
            lastError = error;
            continue;
          }
          throw error;
        }

        if (error instanceof Error) {
          if (error.name === "AbortError") {
            analytics.error("api_request_timeout", new Error(`Request timeout after ${timeout}ms`), { requestId });
            const err = new ApiClientError("Request timeout", 408, "TIMEOUT");
            if (isRetryable(method, 408, "TIMEOUT") && attempt < maxAttempts - 1) {
              lastError = err;
              continue;
            }
            throw err;
          }

          analytics.error("api_network_error", error, { requestId });
          const err = new ApiClientError(error.message, 0, "NETWORK_ERROR");
          if (isRetryable(method, 0, "NETWORK_ERROR") && attempt < maxAttempts - 1) {
            lastError = err;
            continue;
          }
          throw err;
        }

        throw new ApiClientError("Unknown error", 0, "UNKNOWN_ERROR");
      }
    }

    // All retries exhausted — throw the last error
    throw lastError || new ApiClientError("Request failed after retries", 0, "RETRY_EXHAUSTED");
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "GET" });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "DELETE" });
  }

  /** @internal Reset the 401 redirect guard (for testing only) */
  _resetRedirectGuard() {
    this.redirecting = false;
  }

  /**
   * Upload file via multipart form data
   */
  async upload<T>(endpoint: string, file: File, additionalData?: Record<string, string>): Promise<T> {
    const formData = new FormData();
    formData.append("file", file);
    if (additionalData) {
      for (const [key, value] of Object.entries(additionalData)) {
        formData.append(key, value);
      }
    }

    const url = endpoint.startsWith("http") ? endpoint : `${this.baseUrl || ""}${endpoint}`;
    const response = await fetch(url, {
      method: "POST",
      body: formData,
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: { message: response.statusText } })) as ApiErrorPayload;
      const message =
        typeof errorData.error === "string"
          ? errorData.error
          : errorData.error?.message || response.statusText;
      throw new ApiClientError(
        message,
        response.status,
        message || "UPLOAD_ERROR",
      );
    }

    return response.json() as Promise<T>;
  }
}

// Export singleton instance
export const apiClient = new SecureApiClient();

// Export convenience methods
export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) => apiClient.get<T>(endpoint, options),
  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    apiClient.post<T>(endpoint, body, options),
  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    apiClient.put<T>(endpoint, body, options),
  patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    apiClient.patch<T>(endpoint, body, options),
  delete: <T>(endpoint: string, options?: RequestOptions) => apiClient.delete<T>(endpoint, options),
  upload: <T>(endpoint: string, file: File, data?: Record<string, string>) =>
    apiClient.upload<T>(endpoint, file, data),
};
